import { Global } from '@emotion/react'
import { CssBaseline, ThemeProvider } from '@mui/material'
import { AppCacheProvider } from '@mui/material-nextjs/v14-pagesRouter'
import { LicenseInfo } from '@mui/x-license'
import { NextComponentType, NextPageContext } from 'next'
import { AppProps } from 'next/app'
import Head from 'next/head'
import Script from 'next/script'
import { SessionProvider } from 'next-auth/react'
import { appWithTranslation } from 'next-i18next'
import { useEffect } from 'react'
import { loadUsetifulScript } from 'usetiful-sdk'
import { useCookieConsent } from 'components/Cookie/CookieConsent'
import { CookieConsentProvider } from 'components/Cookie/CookieConsentContext'
import { CartProvider } from 'components/Hook/CartContext'
import { LocaleProvider } from 'components/common/LocaleContext'
import i18nConfig from 'next-i18next.config'
import { molitorBold, molitorItalic, nameSans } from 'styles/fonts'
import { globalStyle } from 'styles/globals'
import 'styles/reset.css'
import { theme } from 'styles/theme'

LicenseInfo.setLicenseKey(
  '13c9a99144e78682889b1e857a6a5e27Tz0xMTQ0NjQsRT0xNzgzMzgyMzk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1pbml0aWFsLEtWPTI='
)

const GTM_ID = 'GTM-NLSZ65Z'

function MyApp(props: AppProps) {
  const { Component, pageProps } = props
  useCookieConsent()
  const getLayout =
    // @ts-expect-error getLayout will exist in Next13 on Component types
    Component?.getLayout ||
    ((page: NextComponentType<NextPageContext, any, any>) => page)

  useEffect(() => {
    if (window.location.href.includes('dashboard')) {
      loadUsetifulScript('47e4869e5027438ddf44963b7618a998')
    }
  }, [])

  return (
    <div
      className={`${molitorBold.variable} ${molitorItalic.variable} ${nameSans.variable}`}
    >
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=5"
        />
      </Head>
      <AppCacheProvider {...props}>
        {/* @ts-expect-error */}
        {Component.noGlobalStyles !== true && <Global styles={globalStyle} />}
        <ThemeProvider theme={theme}>
          <SessionProvider>
            <LocaleProvider>
              <CookieConsentProvider>
                <CartProvider>
                  <CssBaseline />
                  {getLayout(<Component {...pageProps}></Component>)}
                  <Script
                    id="gtm-init"
                    strategy="afterInteractive"
                    dangerouslySetInnerHTML={{
                      __html: `
                        window.dataLayer = window.dataLayer || [];
                        window.dataLayer.push({
                          'gtm.start': new Date().getTime(),
                          event: 'gtm.js'
                        });
                        window.gtag = function(){ window.dataLayer.push(arguments); };
                        window.gtag('js', new Date());
                        window.gtag('consent', 'default', {
                          ad_storage: 'denied',
                          ad_user_data: 'denied',
                          ad_personalization: 'denied',
                          analytics_storage: 'denied',
                        });
                        window.gtag('config', '${GTM_ID}', {
                          page_path: window.location.pathname,
                        });
                      `
                    }}
                  />
                  <Script
                    id="gtm"
                    strategy="afterInteractive"
                    src={`https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`}
                  />

                  <Script strategy="lazyOnload" src="/scripts/quanti_1.js" />
                </CartProvider>
              </CookieConsentProvider>
            </LocaleProvider>
          </SessionProvider>
        </ThemeProvider>
      </AppCacheProvider>
    </div>
  )
}
export default appWithTranslation(MyApp, i18nConfig)

export async function getInitialProps() {
  return {
    props: {
      algoliaId: process.env.ALGOLIA_ID,
      algoliaSecret: process.env.ALGOLIA_SECRET
    }
  }
}
